import { createStore } from "./createStore";
import type { SecurityRoomSnapshot } from "../types";
import { api, ApiError } from "../api/client";

// === Re-export so consumers (and tests) can pull the type from this module ===
export type { SecurityRoomSnapshot } from "../types";

export const emptySecurityRoomSnapshot: SecurityRoomSnapshot = {
  summary: {},
  commandAudit: [],
  approvals: [],
  liveActivityEvents: [],
  streamMode: "sse",
};

export const securityRoomStore = createStore<SecurityRoomSnapshot>({
  ...emptySecurityRoomSnapshot,
});

export function resetSecurityRoomStore() {
  securityRoomStore.setState({ ...emptySecurityRoomSnapshot });
}

export function appendSecurityRoomEvent(event: Record<string, unknown>) {
  const eventId =
    typeof event.id === "string" && event.id
      ? event.id
      : `live-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  // Stamp `recordedAt` on any inbound live event that lacks a timestamp.
  // The view's time-range filter ("All time / Last 24h / Last 7d") parses
  // one of `lastSeenAt`/`recordedAt`/`ts`/`createdAt`/`at` per item;
  // events arriving from SSE without any of those would silently disappear
  // under the narrow ranges because `Date.parse(undefined)` is NaN and the
  // filter would treat them as too-old. Server-emitted events that already
  // have a timestamp are left untouched.
  const hasTimestamp =
    typeof event.recordedAt === "string" ||
    typeof event.lastSeenAt === "string" ||
    typeof event.createdAt === "string" ||
    typeof event.ts === "string" ||
    typeof event.at === "string";
  const stampedEvent = hasTimestamp
    ? { ...event, id: eventId }
    : { ...event, id: eventId, recordedAt: new Date().toISOString() };
  securityRoomStore.setState((current) => {
    const liveActivityEvents = [stampedEvent, ...(current.liveActivityEvents || [])];
    const seen = new Set<string>();
    return {
      ...current,
      liveActivityEvents: liveActivityEvents
        .filter((item) => {
          const id = String(item.id || "");
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .slice(0, 200),
      lastEventAt:
        typeof event.recordedAt === "string" ? event.recordedAt : new Date().toISOString(),
    };
  });
}

export function setSecurityRoomStreamMode(mode: "sse" | "poll" | "error") {
  // `error` is transient — used to signal the reconnect loop. The next
  // successful connect overwrites it to "sse" or "poll". The snapshot type
  // includes "error" so the metric pill can show "Reconnecting…" without a
  // dishonest type cast (`as "sse" | "poll"` previously lied about content).
  securityRoomStore.setState({ streamMode: mode });
}

// === Selectors for the Security Room Overview page ===

const DAY_MS = 24 * 60 * 60 * 1000;

// Mirrors the candidate timestamp keys that `appendSecurityRoomEvent` checks.
// If you add a new keys upstream, keep them in sync.
const TIMESTAMP_KEYS = ["recordedAt", "lastSeenAt", "createdAt", "ts", "at"] as const;

function pickTimestamp(item: Record<string, unknown> | undefined): number {
  if (!item) return NaN;
  for (const key of TIMESTAMP_KEYS) {
    const v = item[key];
    if (typeof v === "string") {
      const ts = Date.parse(v);
      if (!Number.isNaN(ts)) return ts;
    }
  }
  return NaN;
}

function isWithin24h(item: Record<string, unknown>, now = Date.now()): boolean {
  const ts = pickTimestamp(item);
  if (Number.isNaN(ts)) return false;
  return now - ts <= DAY_MS;
}

function isBlockSeverity(item: Record<string, unknown>): boolean {
  // CommandEvent uses `decision`; loose stream events use `severity`. Accept both.
  const sev = (item.severity ?? item.decision) as string | undefined;
  return sev === "deny" || sev === "crit";
}

export function selectOpenApprovalsCount(s: SecurityRoomSnapshot): number {
  if (typeof s.summary?.pendingApprovals === "number") return s.summary.pendingApprovals;
  if (Array.isArray(s.pendingApprovals)) return s.pendingApprovals.length;
  return (s.approvals ?? []).filter((a) => (a as Record<string, unknown>).status === "pending")
    .length;
}

// ENFORCED blocks only. An observe-mode verdict records what WOULD have been
// blocked and lets the command run, so counting it here would tell an operator
// that N commands were stopped when some of them executed. The Blocks page
// still lists both — with the observed ones tagged — because the
// shadow-enforcement signal is worth seeing; it is the headline COUNT that
// must not overstate what the engine did.
export function selectBlocks24hCount(s: SecurityRoomSnapshot): number {
  const events = (s.commandAudit ?? []) as Array<Record<string, unknown>>;
  return events.filter((e) => isBlockSeverity(e) && e.enforced !== false && isWithin24h(e)).length;
}

// The would-have-blocked companion, so the page can say "N blocked · M would
// block" instead of silently folding the two together.
export function selectObserved24hCount(s: SecurityRoomSnapshot): number {
  const events = (s.commandAudit ?? []) as Array<Record<string, unknown>>;
  return events.filter((e) => isBlockSeverity(e) && e.enforced === false && isWithin24h(e)).length;
}

export function selectDlpHits24hCount(s: SecurityRoomSnapshot): number {
  const hits =
    (s.dlpHits as Array<Record<string, unknown>> | undefined) ??
    (s.dlpEvents as Array<Record<string, unknown>> | undefined) ??
    (s.dlpFindings as Array<Record<string, unknown>> | undefined) ??
    [];
  return hits.filter((d) => isWithin24h(d)).length;
}

export function selectActiveSessionsCount(s: SecurityRoomSnapshot): number {
  if (typeof s.summary?.highRiskSessions === "number") return s.summary.highRiskSessions;
  return (s.highRiskSessions ?? []).length;
}

export function selectCriticalNow(
  s: SecurityRoomSnapshot,
  max = 3
): Array<Record<string, unknown>> {
  // "Critical · Now" implies recency; without a window filter, stale crit
  // events from days ago surface as if they were live. Keep the page copy
  // and the selector aligned on a 24h window.
  const events = (s.commandAudit ?? []) as Array<Record<string, unknown>>;
  return events
    .filter((e) => isBlockSeverity(e) && isWithin24h(e))
    .sort((a, b) => pickTimestamp(b) - pickTimestamp(a))
    .slice(0, max);
}

export function selectPendingApprovals(
  s: SecurityRoomSnapshot,
  max = 3
): Array<Record<string, unknown>> {
  const candidates =
    (s.pendingApprovals as Array<Record<string, unknown>> | undefined) ??
    ((s.approvals ?? []).filter(
      (a) => (a as Record<string, unknown>).status === "pending"
    ) as Array<Record<string, unknown>>);
  return [...candidates].sort((a, b) => pickTimestamp(b) - pickTimestamp(a)).slice(0, max);
}

export function selectHourlyVerdicts24h(
  s: SecurityRoomSnapshot
): Array<{ hour: number; allow: number; ask: number; deny: number }> {
  const buckets = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    allow: 0,
    ask: 0,
    deny: 0,
  }));
  const now = Date.now();
  const events = (s.commandAudit ?? []) as Array<Record<string, unknown>>;
  for (const e of events) {
    const ts = pickTimestamp(e);
    if (Number.isNaN(ts) || now - ts > DAY_MS) continue;
    const hoursAgo = Math.floor((now - ts) / (60 * 60 * 1000));
    const idx = 23 - hoursAgo;
    if (idx < 0 || idx > 23) continue;
    // Prefer an explicit severity/decision; otherwise derive a verdict from
    // the audit event `type` so the volume chart reflects real activity
    // (terminal.spawn / command.* / dlp.hit / approval-* events carry a type
    // but not always a severity field).
    //
    // "info" is the normalizer saying "this event carries no verdict"
    // (terminal.spawn, terminal.exit, secret-access…). It must NOT fall through
    // to the type heuristic's `else if (type) kind = "allow"` catch-all — that
    // catch-all is what made the chart report 4 blocked commands as
    // "Allowed 10 · Denied 0". A verdict chart counts verdicts; non-verdict
    // activity is simply not plotted.
    const kind = (e.severity ?? e.decision) as string | undefined;
    // A verdict chart plots verdicts. A row that does not carry one is SKIPPED,
    // never guessed at.
    //
    // The old heuristic ended in `else if (type) kind = "allow"`, so any row
    // reaching it with a type and no verdict was drawn in the green "allowed"
    // series — which is how four blocked commands came to be reported as
    // "Allowed 10 · Denied 0". Rows reaching this selector are normalized and
    // carry `severity`, so the heuristic is unnecessary; deleting it means a
    // future writer that bypasses the normalizer cannot reintroduce the
    // failure, because the fallback that made it possible is gone.
    if (kind !== "allow" && kind !== "ask" && kind !== "deny" && kind !== "crit") continue;
    if (kind === "allow") buckets[idx].allow++;
    else if (kind === "ask") buckets[idx].ask++;
    else if (kind === "deny" || kind === "crit") buckets[idx].deny++;
  }
  return buckets;
}

// === Selectors for Real-time pages (PR 2: Approvals, HighRisk, Blocks, DLP) ===

export function selectApprovalsList(s: SecurityRoomSnapshot): Array<Record<string, unknown>> {
  // Prefer backend-pre-filtered pendingApprovals; otherwise filter approvals by status.
  const source =
    (s.pendingApprovals as Array<Record<string, unknown>> | undefined) ??
    ((s.approvals ?? []).filter(
      (a) => (a as Record<string, unknown>).status === "pending"
    ) as Array<Record<string, unknown>>);
  return [...source].sort((a, b) => pickTimestamp(b) - pickTimestamp(a));
}

export function selectHighRiskSessions(
  s: SecurityRoomSnapshot,
  threshold = 70
): Array<Record<string, unknown>> {
  // Backend's highRiskSessions array is already pre-filtered to high-risk.
  // Apply an optional riskScore threshold filter when individual items carry one;
  // items without a riskScore are trusted (backend already classified them).
  const list = (s.highRiskSessions ?? []) as Array<Record<string, unknown>>;
  return list.filter((sess) => {
    const score = sess.riskScore;
    if (typeof score !== "number") return true;
    return score >= threshold;
  });
}

export function selectBlocksList(s: SecurityRoomSnapshot): Array<Record<string, unknown>> {
  // BlocksPage subtitle says "… in the last 24h"; keep the selector aligned
  // so the count and contents match the copy. Older blocks are still in
  // commandAudit and can be surfaced via the AuditPage with a wider range.
  const source = (s.commandAudit ?? []) as Array<Record<string, unknown>>;
  return source
    .filter((e) => isBlockSeverity(e) && isWithin24h(e))
    .sort((a, b) => pickTimestamp(b) - pickTimestamp(a));
}

export function selectDlpHitsByPattern(
  s: SecurityRoomSnapshot
): Map<string, Array<Record<string, unknown>>> {
  // PR #43 introduced redundant aliases for the DLP collection — try each in order.
  const source =
    (s.dlpHits as Array<Record<string, unknown>> | undefined) ??
    (s.dlpEvents as Array<Record<string, unknown>> | undefined) ??
    (s.dlpFindings as Array<Record<string, unknown>> | undefined) ??
    [];
  const out = new Map<string, Array<Record<string, unknown>>>();
  for (const hit of source) {
    const id = typeof hit.patternId === "string" ? hit.patternId : "unknown";
    if (!out.has(id)) out.set(id, []);
    out.get(id)!.push(hit);
  }
  for (const arr of out.values()) {
    arr.sort((a, b) => pickTimestamp(b) - pickTimestamp(a));
  }
  return out;
}

// === Approval actions ===

// A decision goes to the Bun host first, through POST
// /api/approvals/:id/decision. A terminal holding its PTY on an egress ask is
// released only there: the host never reads approval status back from Convex,
// so resolving the Convex row alone leaves the terminal blocked. A bridged
// answer means the host recorded the decision in Convex, as the person
// deciding and with the note as reason, and then released the terminal, so
// nothing is left to do here.
// The page can only reach its own host: a terminal on another machine (an
// admin deciding a teammate's request from the org-wide queue) answers 410
// here and is only resolved in Convex. Releasing it needs the host to read
// approval status from Convex.
//
// A host that answers with its own error is final (see hostRefused). Anything
// else is resolved in Convex: a 410 (no terminal holds that id), or no host
// that can take the decision. A Convex refusal, such as the 403
// "only requester or admin can resolve", rejects so the page can show it.
// Host decisions are "approved"|"denied"; the Convex enum is
// "allowed"|"denied"|"cancelled".
export function approveItem(id: string, note?: string): Promise<void> {
  return decide(id, "allowed", note || "");
}

export function denyItem(id: string, note?: string): Promise<void> {
  return decide(id, "denied", note || "");
}

async function decide(id: string, decision: "allowed" | "denied", note: string): Promise<void> {
  const hostDecision = decision === "allowed" ? "approved" : "denied";
  // A legacy id has no Convex row to fall back on, so the host's answer is
  // final. Checked before the lazy import so a legacy id never fails on a
  // missing Convex configuration it does not need.
  if (!isConvexId(id)) {
    await api.decideHostApproval(id, hostDecision, note);
    return;
  }
  try {
    const res = await api.decideHostApproval(id, hostDecision, note);
    if (res?.bridged) return;
  } catch (err) {
    if (hostRefused(err)) throw err;
  }
  await _convexResolve(id, decision, note);
}

// Convex ids are base32 (no dashes). Legacy ids start with "appr-" or are
// UUIDs; resolve()'s v.id() validation would reject both, so only the host's
// own store can decide one.
function isConvexId(id: string): boolean {
  return typeof id === "string" && !id.includes("-");
}

// The host answered with its own JSON error (`{ error }`), other than the 410
// for an id no terminal holds. A terminal may then be waiting on this
// approval: for a Convex id the route authenticates only when one holds it
// (400, 401, 402, the requester check's 403, and whatever Convex refused when
// the host tried to record the decision, such as a 409, or its 502 when Convex
// was unreachable), and its origin guard or a crash cannot tell. Resolving the
// row in Convex could leave that terminal blocked behind a row that no longer
// shows as pending, so the refusal is final. Every other failure means no host could take the decision, and Convex
// decides it: a network error, Vite's empty 502 with no host behind it, the
// hosted web app's empty 405, or an HTML page in place of the host.
function hostRefused(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status === 410) return false;
  return typeof (err.data as { error?: unknown } | null)?.error === "string";
}

// Lazy-loads convex/react+api so the module remains importable in
// environments without Convex configured.
async function _convexResolve(
  id: string,
  decision: "allowed" | "denied",
  reason: string
): Promise<void> {
  const [{ convex }, { api: cApi }] = await Promise.all([
    import("../lib/convex"),
    import("../lib/convexApi"),
  ]);
  await convex.mutation(cApi.approvals.mutations.resolve, {
    id: id as any,
    decision,
    reason,
  });
}

// snoozeItem was removed in PR #46 review fixes: the v1 no-op exposed a
// misleading button/hotkey on a security-triage queue (operators believed
// they had deferred items that were still pending). Re-export when the
// backend ships real ephemeral snooze state.
